import type { ReactNode } from 'react';

interface CommentModeToolbarProps {
  commentMode: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onSelectCursor: () => void;
  onSelectComment: () => void;
}

function ToolbarButton({
  active,
  disabled,
  title,
  ariaLabel,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  title: string;
  ariaLabel: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={active}
      className="h-[32px] min-w-[32px] px-[8px] inline-flex items-center justify-center rounded-[6px] border-0 transition-[background-color,color] duration-[120ms] disabled:cursor-not-allowed disabled:opacity-45 text-[12px]"
      style={{
        background: active ? '#2563EB' : 'transparent',
        color: active ? '#ffffff' : '#9ca8b6',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={(event) => {
        if (active || disabled) return;
        event.currentTarget.style.background = 'rgba(255,255,255,0.07)';
        event.currentTarget.style.color = '#ffffff';
      }}
      onMouseLeave={(event) => {
        if (active || disabled) return;
        event.currentTarget.style.background = 'transparent';
        event.currentTarget.style.color = '#9ca8b6';
      }}
    >
      {children}
    </button>
  );
}

export default function CommentModeToolbar({
  commentMode,
  disabled = false,
  disabledReason,
  onSelectCursor,
  onSelectComment,
}: CommentModeToolbarProps) {
  return (
    <div
      className="absolute bottom-[24px] left-1/2 z-50 inline-flex -translate-x-1/2 items-center gap-[2px] rounded-[10px] p-[3px]"
      style={{
        background: '#1a1d21',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.06)',
      }}
      aria-label="Artifact tools"
    >
      <ToolbarButton
        active={!commentMode}
        disabled={disabled}
        title="Move (V)"
        ariaLabel="Move tool"
        onClick={onSelectCursor}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M3 2.2V13l3-3 2 4 1.8-.7-2-4 4.2-.6L3 2.2z" />
        </svg>
      </ToolbarButton>

      <span
        className="mx-[2px] h-[18px] w-px"
        style={{ background: 'rgba(255,255,255,0.10)' }}
        aria-hidden="true"
      />

      <ToolbarButton
        active={commentMode}
        disabled={disabled}
        title={disabled ? disabledReason ?? 'Comment mode unavailable' : 'Comment (C)'}
        ariaLabel="Comment tool"
        onClick={onSelectComment}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 3h12v8H5l-3 3V3z" />
        </svg>
      </ToolbarButton>
    </div>
  );
}
